import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Field } from './ui';
import './Form.css';

/**
 * The organisation questions asked in setup and in Organisation settings, so
 * both show the same options and store the same values.
 */

export type Option = { value: string; label: string };

export function ChipChoice({
  label,
  options,
  value,
  onChange,
  stacked,
}: {
  label: string;
  options: Option[];
  value: string | null;
  onChange: (v: string) => void;
  stacked?: boolean;
}) {
  return (
    <fieldset className="choice">
      <legend>{label}</legend>
      <div className={stacked ? 'chips chips-stacked' : 'chips'}>
        {options.map((o) => (
          <button key={o.value} type="button" className="choice-chip" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/** Education setting (L-02): a fixed code, plus the organisation's own words for Other. */
export function EducationSettingField({
  options,
  value,
  other,
  onChange,
  onOtherChange,
  otherError,
}: {
  options: Option[];
  value: string | null;
  other: string;
  onChange: (v: string) => void;
  onOtherChange: (v: string) => void;
  otherError?: string | null;
}) {
  return (
    <div className="org-field">
      <ChipChoice label="Education setting" options={options} value={value} onChange={onChange} />
      {value === 'other' ? (
        <Field
          id="org-setting-other"
          label="Tell us your setting"
          value={other}
          maxLength={120}
          onChange={(e) => onOtherChange(e.target.value)}
          error={otherError}
          autoFocus
        />
      ) : null}
    </div>
  );
}

/**
 * Country: one searchable dropdown (the list will keep growing). The common
 * ones are pinned at the top; typing filters every country.
 */
export function CountryField({
  quick,
  all,
  value,
  onChange,
}: {
  quick: string[];
  all: string[];
  value: string | null;
  onChange: (v: string | null) => void;
  otherOpen?: boolean;
  onOtherOpen?: (open: boolean) => void;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const q = search.trim().toLowerCase();
  const pinned = q ? [] : quick;
  const rest = all.filter((c) => (q ? c.toLowerCase().includes(q) : !quick.includes(c)));
  const options = [...pinned, ...rest];

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => setActive(0), [search]);

  const choose = (c: string) => {
    onChange(c);
    setOpen(false);
    setSearch('');
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (options[active]) choose(options[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const optionId = (i: number) => `${id}-opt-${i}`;

  return (
    <div className="field combo" ref={rootRef}>
      <label id={`${id}-label`} htmlFor={`${id}-button`}>
        Country
      </label>
      <button
        id={`${id}-button`}
        type="button"
        className="combo-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {value ? <span>{value}</span> : <span className="combo-placeholder">Choose a country</span>}
        <svg className="combo-chevron" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <div className="combo-panel">
          <input
            className="combo-search"
            placeholder="Search countries"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onKey}
            autoFocus
            role="combobox"
            aria-expanded="true"
            aria-controls={`${id}-list`}
            aria-activedescendant={options[active] ? optionId(active) : undefined}
            aria-label="Search countries"
          />
          <ul className="combo-list" id={`${id}-list`} role="listbox" aria-labelledby={`${id}-label`}>
            {options.length === 0 ? <li className="combo-empty">No country matches “{search}”.</li> : null}
            {pinned.length ? (
              <li className="combo-group" role="presentation">
                Common
              </li>
            ) : null}
            {options.map((c, i) => (
              <li key={c} role="presentation">
                {i === pinned.length && pinned.length ? <div className="combo-group">All countries</div> : null}
                <div
                  id={optionId(i)}
                  role="option"
                  aria-selected={value === c}
                  className={`combo-option${i === active ? ' is-active' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(c)}
                >
                  {c}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export const STAFF_LABEL = 'Number of staff who will be using EducAitor';

/** Leadership Priority Areas (L-06): up to three in the leader's own words, with suggestions. */
export type PriorityArea = { text: string; suggestion: string | null };

export function PriorityAreasField({
  areas,
  onChange,
  suggestions,
  personal = false,
}: {
  areas: PriorityArea[];
  onChange: (areas: PriorityArea[]) => void;
  suggestions: string[];
  /** Worded for one person's own development rather than a team's priorities. */
  personal?: boolean;
}) {
  const filled = areas.filter((a) => a.text.trim());
  const full = filled.length >= 3;
  const slots: PriorityArea[] = [0, 1, 2].map((i) => areas[i] ?? { text: '', suggestion: null });
  const used = new Set(filled.map((a) => a.text.trim().toLowerCase()));

  const setSlot = (i: number, text: string) => {
    const next = [...slots];
    // Editing a suggestion keeps the link to it until the text is cleared.
    next[i] = { text, suggestion: text.trim() ? next[i].suggestion : null };
    onChange(next);
  };

  const addSuggestion = (s: string) => {
    const i = slots.findIndex((a) => !a.text.trim());
    if (i === -1) return;
    const next = [...slots];
    next[i] = { text: s, suggestion: s };
    onChange(next);
  };

  return (
    <div className="org-field">
      <p className="field-hint">{personal ? 'Up to three, in your own words, or choose from the suggestions below.' : 'Type up to three priority areas in your own words, or choose from the suggested focus areas below.'}</p>
      <div className="priority-inputs">
        {slots.map((a, i) => (
          <Field
            key={i}
            id={`priority-${i + 1}`}
            label={personal ? `Area ${i + 1}` : `Priority area ${i + 1}`}
            value={a.text}
            maxLength={80}
            onChange={(e) => setSlot(i, e.target.value)}
          />
        ))}
      </div>
      <fieldset className="choice">
        <legend>Suggested focus areas</legend>
        <div className="chips" role="group">
          {suggestions.map((s) => {
            const on = used.has(s.toLowerCase());
            return (
              <button
                key={s}
                type="button"
                className="choice-chip"
                aria-pressed={on}
                aria-disabled={!on && full}
                onClick={() => {
                  if (on) {
                    onChange(slots.map((a) => (a.text.trim().toLowerCase() === s.toLowerCase() ? { text: '', suggestion: null } : a)));
                  } else if (!full) addSuggestion(s);
                }}
              >
                {s}
              </button>
            );
          })}
        </div>
      </fieldset>
      <p className="field-hint" aria-live="polite">
        {full ? '3 of 3 added. Remove one to add another.' : `${filled.length} of 3 added`}
      </p>
    </div>
  );
}
